import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, HelpCircle, Loader2, Info, CheckCircle, Trophy, RotateCcw } from 'lucide-react';
import { useAppContext } from '../../../core/App';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';

// NEW INJECTION: Load the 50-item hardcoded Leadership scenario bank
import { leadershipMicroBank } from '../../../data/LeadershipMicroBank';

export default function DynamicSJTGame({ gameData, onComplete }) {
  const { userProfile, authUser } = useAppContext();
  
  // UX Calibration: Stable initialization with Fisher-Yates array shuffle.
  // Secures exactly 4 unpredictable scenarios per session.
  const deckRef = useRef([]);

  if (deckRef.current.length === 0) {
      let array = [...leadershipMicroBank];
      for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
      }
      deckRef.current = array.slice(0, 4);
  }

  const activeDeck = deckRef.current;
  
  // --- STATE ---
  const [stage, setStage] = useState('briefing'); // 'briefing', 'playing', 'intervention', 'results'
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeIntervention, setActiveIntervention] = useState(null);
  const [feedback, setFeedback] = useState(null);
  
  // TELEMETRY: Cognitive Triage Latency & Payload
  const [sessionAttempts, setSessionAttempts] = useState([]);
  const interactionStartTimeRef = useRef(null);

  const activeScenario = activeDeck[currentIndex];

  // Randomize the 4 options visually to prevent position bias
  const shuffledOptions = useMemo(() => {
      if (!activeScenario) return [];
      return [...activeScenario.options].sort(() => Math.random() - 0.5);
  }, [activeScenario]);

  // Handle Mount Lifecycle
  useEffect(() => {
      if (currentIndex === 0 && !activeIntervention && stage !== 'results') {
          setStage('playing');
          interactionStartTimeRef.current = Date.now();
      }
  }, [currentIndex, activeIntervention, stage]);

  const triggerFailure = (option) => {
      const logic = {
          title: "Accountability Failure",
          script: option.feedback || "Your leadership decision failed to execute extreme ownership.",
          videoUrl: null
      };
      setActiveIntervention(logic);
      setStage('intervention');
  };

  const handleOptionSelect = (option) => {
    if (stage !== 'playing') return;
    
    const triageLatency = Date.now() - interactionStartTimeRef.current;

    // Build interaction record
    const interactionRecord = {
        scenarioId: activeScenario.id,
        selectedCategory: option.category,
        isCorrect: option.category === 'Optimal',
        cognitiveTriageLatencyMs: triageLatency,
        timestamp: Date.now()
    };

    setSessionAttempts(prev => [...prev, interactionRecord]);

    if (option.category === 'Optimal') {
        setFeedback({ type: 'success', text: option.feedback });
        
        setTimeout(() => {
            setFeedback(null);
            if (currentIndex + 1 < activeDeck.length) {
                setCurrentIndex(i => i + 1);
                interactionStartTimeRef.current = Date.now(); // Reset TTFI clock
            } else {
                setStage('results');
            }
        }, 1500);

    } else {
        // Immediate hard failure into Tactical Overlay
        triggerFailure(option);
    }
  };

  const handleRetry = () => {
      // Reshuffle the deck on retry to eliminate muscle memory
      let array = [...leadershipMicroBank];
      for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
      }
      deckRef.current = array.slice(0, 4);

      setCurrentIndex(0);
      setActiveIntervention(null);
      setFeedback(null);
      setStage('playing');
      interactionStartTimeRef.current = Date.now();
  };

  const handleFinalSubmit = () => {
      const payload = {
          id: gameData?.id || 'game_leadership_micro',
          rawInteractions: sessionAttempts
      };

      if (typeof onComplete === 'function') {
          onComplete(payload);
      }
  };

  if (stage === 'results') {
      return (
        <div className="h-full flex flex-col items-center justify-center bg-slate-50 rounded-xl p-8 text-center animate-fade-in">
          <div className="mb-6 p-6 rounded-full bg-emerald-100">
             <Trophy className="w-16 h-16 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-black text-gray-900 mb-2 uppercase tracking-tight">Executive Function Verified</h2>
          <p className="text-gray-600 mb-8 max-w-sm">
             You consistently demonstrated extreme ownership and problem-solving without passing the cognitive burden upward.
          </p>
          <button
            onClick={handleFinalSubmit} 
            className="w-full max-w-xs bg-emerald-600 text-white font-black py-4 px-6 rounded-xl shadow-lg hover:bg-emerald-700 transition transform hover:scale-105 flex items-center justify-center gap-2 uppercase tracking-widest"
          >
            <CheckCircle className="w-5 h-5" /> Complete Module
          </button>
        </div>
      );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 rounded-xl overflow-hidden border border-slate-200 shadow-sm relative min-h-[500px] font-sans">
      
      {/* OVERLAY: INTERVENTION */}
      <AnimatePresence>
        {stage === 'intervention' && activeIntervention && (
            <div className="absolute inset-0 z-[100]">
                <TacticalOverlay 
                    type="intervention" 
                    data={activeIntervention} 
                    onRetry={handleRetry} 
                />
            </div>
        )}
      </AnimatePresence>

      {/* HEADER */}
      <div className="p-5 border-b bg-white flex justify-between items-center z-10 shadow-sm">
        <div>
           <h3 className="font-black text-slate-800 uppercase tracking-tight flex items-center gap-2">
               <ShieldCheck className="text-blue-600" /> Executive Problem Solving
           </h3>
           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Extreme Ownership Protocol</p>
        </div>
        <div className="flex items-center gap-2">
           {/* ADA COMPLIANCE: No visible timer. */}
           <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-bold mr-2">
                Scenario {currentIndex + 1}/{activeDeck.length}
           </span>
        </div>
      </div>

      {/* SCENARIO CONTENT */}
      <div className="p-4 md:p-8 flex-grow overflow-y-auto">
         <div className="max-w-2xl mx-auto flex flex-col gap-6 relative">
             
             {/* The Prompt */}
             <div className="p-6 bg-slate-900 text-white rounded-2xl shadow-lg border border-slate-700">
                 <h4 className="text-[10px] text-emerald-400 font-black uppercase tracking-widest mb-3 flex items-center gap-1.5">
                     <Info size={12} /> High-Stakes Context
                 </h4>
                 <p className="text-lg leading-relaxed font-medium">
                     {activeScenario.scenarioContext}
                 </p>
             </div>

             {/* The Options Grid */}
             <div className="space-y-4">
                 <AnimatePresence mode="wait">
                     <motion.div key={activeScenario.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                         {shuffledOptions.map((option, idx) => (
                             <button
                                 key={idx}
                                 disabled={!!feedback}
                                 onClick={() => handleOptionSelect(option)}
                                 className="w-full text-left p-5 bg-white border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-md transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                             >
                                 <div className="flex items-start gap-4">
                                     <div className="mt-1 w-5 h-5 rounded border-2 border-slate-300 group-hover:border-blue-500 flex-shrink-0" />
                                     <span className="text-slate-700 font-medium leading-relaxed group-hover:text-blue-900">
                                         {option.actionText}
                                     </span>
                                 </div>
                             </button>
                         ))}
                     </motion.div>
                 </AnimatePresence>
             </div>
             
             {/* Instant Positive Feedback */}
             <AnimatePresence>
                {feedback && feedback.type === 'success' && (
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="absolute bottom-0 left-0 right-0 p-5 bg-emerald-50 border-t-4 border-emerald-500 rounded-b-xl flex items-start gap-3 shadow-2xl"
                    >
                        <CheckCircle className="text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <h4 className="font-black text-xs text-emerald-800 uppercase tracking-widest mb-1">Optimal Action Confirmed</h4>
                            <p className="text-sm text-emerald-900 font-medium">{feedback.text}</p>
                        </div>
                    </motion.div>
                )}
             </AnimatePresence>
         </div>
      </div>
      
      <div className="bg-slate-50 p-4 border-t border-slate-200 text-center z-10">
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
              JobVantage Corporate Survival Matrix v1.2
          </p>
      </div>
    </div>
  );
}